import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Sparkles, Loader2, X, Save, CalendarPlus, Copy, MessageCircle, ExternalLink,
    User, GraduationCap, Video, Info,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useBodyScrollLock } from "../../lib/useBodyScrollLock";

/**
 * V168 — Create Demo Class Wizard.
 *
 * One clean form. Admin fills in student + tutor + schedule + optional
 * meeting URL. On submit, backend creates:
 *   1. an Assignment (marked `is_demo=true`)
 *   2. a `class_schedules` row for session #1
 *   3. fresh parent + tutor portal tokens
 * Then this dialog shows a success screen with WhatsApp-ready Copy /
 * WhatsApp buttons for both portal links.
 *
 * The admin never has to visit the tutor/parent portal — they just paste
 * the links into WhatsApp and both parties tap Join Class when it's time.
 */

const APP_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function CreateDemoClassDialog({ onClose, onCreated }) {
    // Step: "form" → fills the form. "success" → shows portal links + copy/WA.
    const [step, setStep] = useState("form");
    const [saving, setSaving] = useState(false);
    useBodyScrollLock(true);
    const [result, setResult] = useState(null); // { assignment, schedule, parent_portal_url, tutor_portal_url }

    // Tutor picker
    const [tutorQuery, setTutorQuery] = useState("");
    const [tutorOptions, setTutorOptions] = useState([]);
    const [tutorLoading, setTutorLoading] = useState(false);

    const [f, setF] = useState({
        student_name: "",
        parent_name: "",
        parent_phone: "",
        parent_email: "",
        grade: "",
        board: "CBSE",
        subject: "",
        city: "",
        tutor_id: "",
        tutor_name: "",
        scheduled_start: defaultStartLocal(),
        duration_minutes: 45,
        meeting_url: "",
        tutor_note: "",
        coordinator_notes: "",
    });

    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

    // Debounced tutor search — hits the existing tutor-applications
    // endpoint which returns the approved tutor pool.
    useEffect(() => {
        const q = tutorQuery.trim();
        if (q.length < 2) { setTutorOptions([]); return; }
        let cancelled = false;
        setTutorLoading(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/admin/tutor-applications?status=approved&search=${encodeURIComponent(q)}`);
                if (cancelled) return;
                const opts = (r.data?.items || []).slice(0, 8).map((t) => ({
                    id: t.id,
                    name: t.name || t.tutor_name || "(unnamed)",
                    email: t.email || "",
                    phone: t.phone || "",
                    subjects: t.subjects || t.expertise || "",
                }));
                setTutorOptions(opts);
            } catch (_e) { setTutorOptions([]); }
            finally { if (!cancelled) setTutorLoading(false); }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [tutorQuery]);

    const pickTutor = (t) => {
        set("tutor_id", t.id);
        set("tutor_name", t.name);
        setTutorQuery(t.name);
        setTutorOptions([]);
    };

    const canSubmit = f.student_name.trim().length >= 2
        && (f.tutor_id || f.tutor_name.trim().length >= 2)
        && f.scheduled_start
        && f.duration_minutes > 0
        && !saving;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const url = (f.meeting_url || "").trim();
        if (url && !/^https?:\/\//i.test(url)) {
            toast.error("Meeting URL must start with http:// or https://");
            return;
        }
        setSaving(true);
        try {
            const iso = new Date(f.scheduled_start).toISOString();
            // If admin didn't pick from the dropdown, we synth a tutor id so
            // the demo can still proceed. Admins can link a proper tutor
            // profile later from the Assignment Detail modal.
            const tutorId = f.tutor_id || `demo-tutor-${Date.now().toString(36)}`;
            const r = await api.post("/admin/demo-class/quick-create", {
                student_name: f.student_name.trim(),
                parent_name: f.parent_name.trim() || null,
                parent_phone: f.parent_phone.trim() || null,
                parent_email: f.parent_email.trim() || null,
                grade: f.grade.trim() || null,
                board: f.board.trim() || null,
                subject: f.subject.trim() || null,
                city: f.city.trim() || null,
                tutor_id: tutorId,
                tutor_name: f.tutor_name.trim(),
                scheduled_start: iso,
                duration_minutes: Number(f.duration_minutes),
                meeting_url: url || null,
                tutor_note: f.tutor_note.trim() || null,
                coordinator_notes: f.coordinator_notes.trim() || null,
            });
            toast.success("Demo class created — share the links!");
            setResult(r.data);
            setStep("success");
            onCreated?.(r.data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not create demo"));
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[95] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            style={{ width: "100vw", height: "100vh" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-wizard-title"
            data-testid="demo-class-wizard"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div
                className="bg-white w-full sm:w-auto sm:max-w-lg sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-fuchsia-600 font-bold flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Demo Class
                        </div>
                        <div id="demo-wizard-title" className="text-lg font-heading font-bold text-[#0F172A]">
                            {step === "form" ? "One-click demo setup" : "All set — share the links"}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="demo-wizard-close"
                        data-cm-compact
                        aria-label="Close"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {step === "form" ? (
                    <form onSubmit={submit}>
                        <div className="p-4 sm:p-6 space-y-5">
                            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 text-xs text-cyan-900 flex items-start gap-2">
                                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                                <div>
                                    Anand Tutorials mediates every demo. Parents & tutors won't see each other's contact — they'll join via secure portal links generated here.
                                </div>
                            </div>

                            <Section icon={User} title="Student & parent">
                                <Grid>
                                    <F label="Student name *">
                                        <input value={f.student_name} onChange={(e) => set("student_name", e.target.value)} data-testid="demo-student-name" className={inp} placeholder="Rohan Sharma" />
                                    </F>
                                    <F label="Parent name">
                                        <input value={f.parent_name} onChange={(e) => set("parent_name", e.target.value)} data-testid="demo-parent-name" className={inp} placeholder="Mr / Ms Sharma" />
                                    </F>
                                    <F label="Parent phone">
                                        <input value={f.parent_phone} onChange={(e) => set("parent_phone", e.target.value)} data-testid="demo-parent-phone" className={inp} inputMode="tel" placeholder="+91 98xxx xxxxx" />
                                    </F>
                                    <F label="Parent email">
                                        <input value={f.parent_email} onChange={(e) => set("parent_email", e.target.value)} data-testid="demo-parent-email" className={inp} inputMode="email" placeholder="optional" />
                                    </F>
                                    <F label="Grade / Class">
                                        <input value={f.grade} onChange={(e) => set("grade", e.target.value)} data-testid="demo-grade" className={inp} placeholder="10" />
                                    </F>
                                    <F label="Board">
                                        <select value={f.board} onChange={(e) => set("board", e.target.value)} data-testid="demo-board" className={inp}>
                                            <option value="CBSE">CBSE</option>
                                            <option value="ICSE">ICSE</option>
                                            <option value="IGCSE">IGCSE</option>
                                            <option value="IB">IB</option>
                                            <option value="State">State</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </F>
                                    <F label="Subject">
                                        <input value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="demo-subject" className={inp} placeholder="Math" />
                                    </F>
                                    <F label="City">
                                        <input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="demo-city" className={inp} placeholder="Bengaluru" />
                                    </F>
                                </Grid>
                            </Section>

                            <Section icon={GraduationCap} title="Assigned tutor">
                                <div className="relative">
                                    <input
                                        value={tutorQuery}
                                        onChange={(e) => { setTutorQuery(e.target.value); set("tutor_id", ""); set("tutor_name", e.target.value); }}
                                        data-testid="demo-tutor-search"
                                        placeholder="Type tutor name (min 2 chars)…"
                                        className={inp}
                                    />
                                    {tutorLoading && (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-3 top-3.5 text-slate-400" />
                                    )}
                                    {tutorOptions.length > 0 && (
                                        <ul className="absolute z-10 top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg" data-testid="demo-tutor-options">
                                            {tutorOptions.map((t) => (
                                                <li key={t.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => pickTutor(t)}
                                                        data-testid={`demo-tutor-option-${t.id}`}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 border-b last:border-b-0 border-slate-100"
                                                    >
                                                        <div className="font-semibold text-[#0F172A] truncate">{t.name}</div>
                                                        <div className="text-[11px] text-slate-500 truncate">
                                                            {[t.subjects, t.email].filter(Boolean).join(" · ")}
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                {f.tutor_id ? (
                                    <div className="text-[11px] text-emerald-700 mt-1">✓ Selected tutor: <strong>{f.tutor_name}</strong></div>
                                ) : tutorQuery.trim().length >= 2 ? (
                                    <div className="text-[11px] text-amber-700 mt-1">
                                        Tutor not in system? We'll create the demo with <strong>{tutorQuery.trim()}</strong> as the name — link a full tutor profile from the Assignment page later.
                                    </div>
                                ) : (
                                    <div className="text-[11px] text-slate-500 mt-1">Pick from the dropdown, or type a name — a full profile can be linked later.</div>
                                )}
                            </Section>

                            <Section icon={Video} title="Session & meeting">
                                <Grid>
                                    <F label="Start date & time *">
                                        <input type="datetime-local" value={f.scheduled_start} onChange={(e) => set("scheduled_start", e.target.value)} data-testid="demo-start" className={inp} />
                                    </F>
                                    <F label="Duration (mins)">
                                        <input type="number" min={5} max={600} step={5} value={f.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} data-testid="demo-duration" className={inp} />
                                    </F>
                                </Grid>
                                <F label="Meeting URL (Zoom · Meet · Teams · Jitsi)">
                                    <input type="url" value={f.meeting_url} onChange={(e) => set("meeting_url", e.target.value)} data-testid="demo-meeting-url" className={inp} placeholder="https://meet.google.com/abc-defg-hij" />
                                    <div className="text-[10px] text-slate-500 mt-1">
                                        {f.meeting_url.trim() ? "Portal Join Class buttons will open this URL in a new tab." : "Empty → parents/tutors will use the native in-platform video (needs 100ms enabled)."}
                                    </div>
                                </F>
                                <F label="Note for tutor / student">
                                    <textarea rows={2} maxLength={400} value={f.tutor_note} onChange={(e) => set("tutor_note", e.target.value)} data-testid="demo-tutor-note" className={`${inp} resize-none`} placeholder="e.g., Please assess Ch 3 fundamentals" />
                                </F>
                                <F label="Coordinator notes (internal)">
                                    <textarea rows={2} maxLength={1000} value={f.coordinator_notes} onChange={(e) => set("coordinator_notes", e.target.value)} data-testid="demo-coord-notes" className={`${inp} resize-none`} placeholder="Internal — only admins see this" />
                                </F>
                            </Section>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-6 py-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={saving}
                                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="demo-wizard-submit"
                                data-cm-primary
                                className="inline-flex items-center gap-1.5 bg-fuchsia-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-fuchsia-700 disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                                Create demo class
                            </button>
                        </div>
                    </form>
                ) : (
                    <SuccessScreen result={result} onClose={onClose} />
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------- Success screen

function SuccessScreen({ result, onClose }) {
    if (!result) return null;
    const parentUrl = `${APP_ORIGIN}${result.parent_portal_url}`;
    const tutorUrl = `${APP_ORIGIN}${result.tutor_portal_url}`;
    const a = result.assignment || {};
    const s = result.schedule || {};

    return (
        <div className="p-4 sm:p-6 space-y-5" data-testid="demo-wizard-success">
            <div className="bg-gradient-to-br from-fuchsia-50 to-cyan-50 border border-fuchsia-200 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold">Demo class created</div>
                <div className="text-lg font-heading font-bold text-[#0F172A] mt-0.5">
                    {a.student_name} · {a.subject || "—"} · {a.grade || "—"}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                    Tutor: <strong>{a.tutor_name}</strong> · {formatWhen(s.scheduled_start, s.duration_mins)}
                </div>
                <div className="text-[11px] text-slate-600 mt-1">Assignment code: <span className="font-mono">{a.assignment_code}</span></div>
            </div>

            <LinkCard
                title="Parent / Student portal link"
                subtitle="Share with the parent — Join Class button appears at start time"
                url={parentUrl}
                waMessage={buildWaMessage("parent", a, s, parentUrl)}
                phone={a.parent_phone || ""}
                testidPrefix="demo-link-parent"
            />

            <LinkCard
                title="Tutor portal link"
                subtitle="Share with the tutor — they'll see the session card in their portal"
                url={tutorUrl}
                waMessage={buildWaMessage("tutor", a, s, tutorUrl)}
                phone={""}   /* tutor phone not stored on assignment for privacy */
                testidPrefix="demo-link-tutor"
            />

            <div className="flex flex-wrap gap-2 pt-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg"
                    data-testid="demo-wizard-done"
                >
                    Done
                </button>
                <Link
                    to="/admin/dashboard"
                    className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1E293B]"
                    data-testid="demo-wizard-goto-dashboard"
                >
                    Go to dashboard
                </Link>
            </div>
        </div>
    );
}

function LinkCard({ title, subtitle, url, waMessage, phone, testidPrefix }) {
    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch (_e) {
            toast.error("Copy failed — long-press the text to select");
        }
    };
    const wa = () => {
        const digits = (phone || "").replace(/[^0-9]/g, "");
        const link = digits
            ? `https://wa.me/${digits}?text=${encodeURIComponent(waMessage)}`
            : `https://wa.me/?text=${encodeURIComponent(waMessage)}`;
        window.open(link, "_blank", "noopener,noreferrer");
    };
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-3.5" data-testid={testidPrefix}>
            <div className="text-sm font-heading font-bold text-[#0F172A]">{title}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
            <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-[11px] break-all text-slate-700 font-mono">
                {url}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
                <button type="button" onClick={() => copy(url, "Link")} data-testid={`${testidPrefix}-copy-link`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-slate-200 hover:border-cyan-300 px-2.5 py-1.5 rounded"><Copy className="w-3 h-3" /> Copy link</button>
                <button type="button" onClick={() => copy(waMessage, "Message")} data-testid={`${testidPrefix}-copy-msg`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-slate-200 hover:border-cyan-300 px-2.5 py-1.5 rounded"><Copy className="w-3 h-3" /> Copy WA message</button>
                <button type="button" onClick={wa} data-testid={`${testidPrefix}-whatsapp`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-[#25D366] text-white hover:brightness-95 px-2.5 py-1.5 rounded"><MessageCircle className="w-3 h-3" /> WhatsApp</button>
                <a href={url} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-open`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-slate-200 hover:border-cyan-300 px-2.5 py-1.5 rounded"><ExternalLink className="w-3 h-3" /> Open portal</a>
            </div>
        </div>
    );
}

// ---------------------------------------------------------- helpers

function Section({ icon: Icon, title, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
                <Icon className="w-3 h-3" /> {title}
            </div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}
function Grid({ children }) {
    return <div className="grid grid-cols-2 gap-3">{children}</div>;
}
function F({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
            {children}
        </label>
    );
}
const inp = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none bg-white";

function defaultStartLocal() {
    // Default to next day at 6 PM local — most demo requests want that slot.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(18, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatWhen(iso, mins) {
    try {
        const d = new Date(iso);
        const opts = { weekday: "short", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" };
        return `${d.toLocaleString(undefined, opts)} · ${mins || 45} min`;
    } catch { return iso; }
}

function buildWaMessage(party, a, s, url) {
    const when = formatWhen(s.scheduled_start, s.duration_mins);
    if (party === "parent") {
        return `Hi ${a.parent_name || "there"} 👋

Your Anand Tutorials demo class is confirmed:

📚 ${a.subject || "Subject"} · Grade ${a.grade || "—"}
👩‍🏫 Tutor: ${a.tutor_name}
🗓️ ${when}

Tap the link below to open your portal and join the class. Save it for future sessions too:

${url}

Any questions? Just reply here — Team Anand Tutorials`;
    }
    return `Hi ${a.tutor_name} 👋

You have a demo class scheduled with Anand Tutorials:

📚 ${a.subject || "Subject"} · Grade ${a.grade || "—"} (${a.board || "—"})
👦 Student: ${a.student_name}
🗓️ ${when}

Please open your tutor portal here to view session details and join the class:

${url}

The parent's contact stays private — coordinate any changes with us. Thanks!
— Anand Tutorials`;
}
